/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { useHistoryPush } from "utilities/NavigationUtilities";
import { useRouteMatch } from "react-router-dom";
import { useCallback } from "react";

export enum ExportPages {
  INDEX = "index",
  AUDIOGRAMS = "audiograms",
  VIDEOS = "videos",
}

const SELECTIVE_EXPORT_QUERY_PARAM = "selective";
const LOAD_WITH_CAPTIONS_QUERY_PARAM = "load_with_captions";
const FROM_USE_CASE_PAGE_QUERY_PARAM = "usecase";

export const NavigationOptionKeys = {
  SELECTIVE: SELECTIVE_EXPORT_QUERY_PARAM,
  LOAD_WITH_CAPTIONS: LOAD_WITH_CAPTIONS_QUERY_PARAM,
  FROM_USECASE: FROM_USE_CASE_PAGE_QUERY_PARAM,
} as const;

export type NavigationOptionsParams = Partial<{
  [SELECTIVE_EXPORT_QUERY_PARAM]: string | boolean;
  [LOAD_WITH_CAPTIONS_QUERY_PARAM]: string | boolean;
  [FROM_USE_CASE_PAGE_QUERY_PARAM]: string | boolean;
}>;

export type NavigationOptions = {
  [SELECTIVE_EXPORT_QUERY_PARAM]: boolean;
  [LOAD_WITH_CAPTIONS_QUERY_PARAM]: boolean;
  [FROM_USE_CASE_PAGE_QUERY_PARAM]: boolean;
};

export type ExportNavigationHook = {
  currentExportsPage: ExportPages | null; // Null if we're not actually in the exports sub-router right now
  navigationOptions: NavigationOptions;
  goToExportPage: (options: {
    page: ExportPages;
    options?: NavigationOptionsParams;
    projectUuid?: string;
  }) => void;
};

export const useExportNavigation = (): ExportNavigationHook => {
  const { path, params } = useRouteMatch<{ projectUuid: string }>();
  const projectUuidFromPath = params.projectUuid;
  const push = useHistoryPush();

  const currentExportsPage = ((): ExportPages | null => {
    if (!path.includes("/export")) {
      return null;
    }
    if (path.includes(`/${ExportPages.AUDIOGRAMS}`)) {
      return ExportPages.AUDIOGRAMS;
    } else if (path.includes(`/${ExportPages.VIDEOS}`)) {
      return ExportPages.VIDEOS;
    } else {
      return ExportPages.INDEX;
    }
  })();

  const goToExportPage = useCallback(
    ({
      page,
      options = {},
      projectUuid = projectUuidFromPath,
    }: {
      page: ExportPages;
      options?: NavigationOptionsParams;
      projectUuid?: string;
    }) => {
      if (!projectUuid) {
        console.error(
          "projectUuid is not defined, cannot navigate to export page."
        );
        return;
      }

      // Build query string from existing URL params and provided options
      const currentUrlParams = new URLSearchParams(window.location.search);

      // Add all provided query params (these will override existing ones with same key)
      Object.entries(options).forEach(([key, value]) => {
        currentUrlParams.set(key, String(value));
      });

      const queryString = currentUrlParams.toString();
      const queryParamString = queryString ? `?${queryString}` : "";

      switch (page) {
        case ExportPages.INDEX:
          push(`/projects/${projectUuid}/export` + queryParamString);
          break;
        case ExportPages.AUDIOGRAMS:
          push(`/projects/${projectUuid}/export/audiograms` + queryParamString);
          break;
        case ExportPages.VIDEOS:
          push(`/projects/${projectUuid}/export/videos` + queryParamString);
          break;
        default:
          break;
      }
    },
    [push, projectUuidFromPath]
  );

  const queryStringParams = new URLSearchParams(window.location.search);
  const isSelectiveExport =
    queryStringParams.get(SELECTIVE_EXPORT_QUERY_PARAM) === "true";
  const loadWithCaptions =
    queryStringParams.get(LOAD_WITH_CAPTIONS_QUERY_PARAM) === "true";
  const fromUseCasePage =
    queryStringParams.get(FROM_USE_CASE_PAGE_QUERY_PARAM) === "true";

  return {
    currentExportsPage,
    navigationOptions: {
      [SELECTIVE_EXPORT_QUERY_PARAM]: isSelectiveExport,
      [LOAD_WITH_CAPTIONS_QUERY_PARAM]: loadWithCaptions,
      [FROM_USE_CASE_PAGE_QUERY_PARAM]: fromUseCasePage,
    },
    goToExportPage,
  };
};
